/**
 * Transaction Status Page - Standalone status page for external partner apps
 *
 * Accessed via URL params: transaction_id, token, lang, refNumber
 * Fetches transaction data from API and displays status information
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import type { TransactionStatusResponse } from "@/api";
import { StatusComponent as Status } from "@/components/ramps/Status";
import { RampsText } from "@/components/ramps/Text";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { env } from "@/config/env";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useAnalytics } from "@/hooks/usePostHog";
import { isHoldStatus } from "@/lib/get-status-label";
import {
	getApiVersionFromSearchParams,
	getAuthTokenFromSearchParams,
	validateRampsSearch,
} from "@/lib/ramps-search-schema";
import { convertTransactionDataToStatusDetails } from "@/lib/status-utils";
import { useWebViewIntegration } from "@/lib/url-state";
import {
	hasCancellationBeenSubmitted,
	rampsActions,
	useRampsStore,
} from "@/stores/use-ramps-store";
import type { ExitEventProperties } from "@/utils/analytics";
import { ANALYTICS_EVENTS } from "@/utils/analytics";
import { RampsButton } from "../components/ramps/Button";

export const Route = createFileRoute("/transaction-status")({
	validateSearch: (search) =>
		search as {
			transaction_id: string;
			token?: string;
			accesstoken?: string;
			lang?: string;
			refNumber?: string; // Make refNumber optional
			wallet_provider?: string;
		},
	component: TransactionStatusPage,
});

function TransactionStatusPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const store = useRampsStore();
	const { sendToWebView } = useWebViewIntegration();
	const { trackEvent, trackPageView, trackExit } = useAnalytics();

	// Guards the hold event against re-fires: the effect below re-runs whenever
	// trackEvent's identity changes (it depends on store.currentStep) and on
	// every status refetch, which would otherwise double-count the impression.
	const holdTrackedRef = useRef(false);

	// Get transaction ID from URL or store (v3 may have it from prepare endpoint)
	const transactionId = search.transaction_id || store.transactionId;

	// Validate search params to ensure type safety
	const validatedSearch = validateRampsSearch({
		...search,
		transaction_id: transactionId || "",
	});

	// Get auth token (supports v2, v3, and v5 API)
	const authToken =
		getAuthTokenFromSearchParams(validatedSearch) || store.token;

	// Store the token so the components below (pickup locations) can authenticate
	if (authToken) {
		rampsActions.setToken(authToken);
	}

	// Detect API version synchronously BEFORE creating API client
	// This ensures the API client uses the correct version on first render
	// Transaction status page is standalone, so pass isStandaloneTransactionStatus=true
	const isStandaloneTransactionStatus = true;
	const detectedVersionFromUrl = getApiVersionFromSearchParams(
		validatedSearch,
		isStandaloneTransactionStatus,
	);
	// Use detected version if explicit, otherwise fall back to store or default
	const detectedVersion = detectedVersionFromUrl ?? store.apiVersion ?? "v3";

	// CRITICAL: Set API version synchronously BEFORE creating API client
	// This ensures the API client uses the correct version on first render
	if (authToken && store.apiVersion !== detectedVersion) {
		rampsActions.setApiVersion(detectedVersion);
		if (env.isDevelopment) {
			const versionSource =
				detectedVersion === "v5"
					? "accesstoken (OAuth)"
					: detectedVersion === "v2"
						? search.refNumber
							? "token + refNumber (JWT v2 standalone)"
							: validatedSearch.operation &&
									validatedSearch.wallet_provider &&
									validatedSearch.asset_code
								? "token + URL context params (JWT v2)"
								: "token on standalone status page (JWT v2 fallback)"
						: "token only (JWT v3)";
			console.log(
				`🔍 Transaction Status Page - API Version detected: ${detectedVersion} from: ${versionSource}`,
			);
		}
	}

	const { apiClient } = useApiClientWithAutoVersion({
		authToken: authToken || "",
		isStandaloneTransactionStatus: true,
	});

	// Use detectedVersion first (from current URL params), fall back to store only if detection was null
	// This ensures we use the correct version for the current page, not a stale store value
	const apiVersion = detectedVersion ?? store.apiVersion ?? "v3";
	const isV3 = apiVersion === "v3";

	// Prepare status for v3 API ONLY (v2 and v5 do not have prepareStatus endpoint)
	// This endpoint is called before GET status for v3 API standalone status page
	const {
		data: prepareStatusData,
		isLoading: isPreparingStatus,
		error: prepareStatusError,
	} = useQuery({
		queryKey: ["prepare-status", transactionId, authToken, apiVersion],
		queryFn: async () => {
			// Double-check: only call prepareStatus for v3
			if (apiVersion !== "v3") {
				throw new Error("prepareStatus is only available for v3 API");
			}
			return await apiClient.transaction.prepareStatus();
		},
		enabled: apiVersion === "v3" && !!transactionId && !!authToken,
	});

	// Fetch transaction data
	// For v2: refNumber is in URL params (standalone status page pattern)
	// For v3: refNumber is not in URL, API returns referenceNumber in response
	//   - First call prepareStatus, then get status
	// For v5: refNumber may be in URL params
	const {
		data: transactionData,
		isLoading: isLoadingStatus,
		error,
	} = useQuery({
		queryKey: [
			"transaction-status",
			transactionId,
			search.refNumber || "no-ref",
			isV3 ? prepareStatusData?.transactionId : undefined,
		],
		queryFn: async () => {
			return (await apiClient.transaction.getTransactionStatus(
				transactionId,
				search.refNumber, // Optional - v3 doesn't require it, v5 may have it
				authToken || "",
			)) as TransactionStatusResponse;
		},
		enabled:
			!!transactionId &&
			!!authToken &&
			(!isV3 || (!!prepareStatusData && !prepareStatusError)), // For v3, wait for prepareStatus and ensure no error
	});

	const isLoading = isPreparingStatus || isLoadingStatus;
	const hasError = prepareStatusError || error;

	// RAMPS-4268: hydrate the store with the prepareStatus response so the
	// active locale matches the partner-supplied lang on the transaction
	// (e.g. JWT data.lang=fr). The store guard added in setPrepareData
	// already refuses to overwrite a URL-driven locale, so this is safe to
	// always call — it's a no-op when the partner passed `?app.languages=`
	// or `?lang=` on the iframe URL.
	useEffect(() => {
		if (!prepareStatusData) return;
		rampsActions.setPrepareData({
			operation: prepareStatusData.operation,
			lang: prepareStatusData.lang,
			assetCode: prepareStatusData.assetCode,
			transactionId: prepareStatusData.transactionId,
			walletProvider: prepareStatusData.walletProvider,
			amount: prepareStatusData.amount,
		});
	}, [prepareStatusData]);

	// Activate the Lingui catalog matching whatever lang ended up in the
	// store (which is either the URL-driven choice or the prepareStatus
	// value). Status pages don't mount PrepareInitializer, so we do this
	// inline. Stays in lockstep with store.lang so URL overrides win.
	useEffect(() => {
		if (!store.lang) return;
		// Lazy import so the route bundle doesn't pull i18n-loader eagerly.
		void import("@/lib/i18n-loader").then(
			({ switchLanguage, getCurrentLanguage }) =>
				import("@/lib/language-detection").then(
					({ parseSupportedLanguageFromString }) => {
						const target = parseSupportedLanguageFromString(store.lang);
						if (getCurrentLanguage() === target) return;
						void switchLanguage(target);
					},
				),
		);
	}, [store.lang]);

	// PostHog: page view and completion/cancel events when transaction data has loaded
	useEffect(() => {
		if (!transactionData || isLoading) return;

		const status = transactionData.transactionStatus ?? "";
		const kindUpper = transactionData.kind?.toUpperCase();
		const isDeposit = kindUpper === "DEPOSIT";
		const referenceNumber =
			isDeposit &&
			(transactionData.confirmationNumber || transactionData.confirmationCode)
				? transactionData.confirmationNumber || transactionData.confirmationCode
				: (transactionData.referenceNumber ?? "");

		// Page view: standalone status page loaded with transaction data
		trackPageView("transaction_status", {
			page_name: "transaction_status",
			wallet_provider: search.wallet_provider,
			transaction_id: transactionId,
			transaction_status: status,
		} as Parameters<typeof trackPageView>[1]);
		trackEvent(ANALYTICS_EVENTS.PAGE_TRANSACTION_STATUS_VIEWED, {
			wallet_provider: search.wallet_provider,
			transaction_id: transactionId,
			transaction_status: status,
			sub_status: transactionData.substatus?.[0] ?? "",
			reference_number: referenceNumber,
		});

		// Hold: transaction held for manual review, so the user is shown the hold
		// contact notice. Fired once per page load (see holdTrackedRef).
		if (
			isHoldStatus(status, transactionData.substatus?.[0]) &&
			!holdTrackedRef.current
		) {
			holdTrackedRef.current = true;
			trackEvent(ANALYTICS_EVENTS.TRANSACTION_HOLD_VIEWED, {
				wallet_provider: search.wallet_provider,
				transaction_id: transactionId,
				transaction_status: status,
				hold_reason: transactionData.substatus?.[0] ?? "",
				reference_number: referenceNumber,
				operation: isDeposit ? "deposit" : "withdraw",
			});
		}

		// Completion: primary place to capture transaction_completed and funds_deposited/funds_collected
		if (status === "COMPLETED" || status === "SUCCESS") {
			const exitProps: ExitEventProperties = {
				exit_type: "completed",
				transaction_id: transactionId,
				transaction_status: status,
				exit_reason: "transaction_completed_successfully",
			};
			trackExit("completed", exitProps);
			if (isDeposit) {
				trackEvent(ANALYTICS_EVENTS.FUNDS_DEPOSITED, {
					transaction_id: transactionId,
					transaction_status: status,
					reference_number: referenceNumber,
					wallet_provider: search.wallet_provider,
				});
			} else {
				trackEvent(ANALYTICS_EVENTS.FUNDS_COLLECTED, {
					transaction_id: transactionId,
					transaction_status: status,
					reference_number: referenceNumber,
					wallet_provider: search.wallet_provider,
				});
			}
		}

		// Optional: capture cancellation when user returns to standalone page and sees CANCELLED/FAILED
		if (
			status === "CANCELLED" ||
			status === "CANCELED" ||
			status === "FAILED"
		) {
			const exitProps: ExitEventProperties = {
				exit_type: "cancelled",
				transaction_id: transactionId,
				transaction_status: status,
				exit_reason: "transaction_cancelled_or_failed",
			};
			trackExit("cancelled", exitProps);
		}
	}, [
		transactionData,
		isLoading,
		transactionId,
		search.wallet_provider,
		trackEvent,
		trackPageView,
		trackExit,
	]);

	const handleClose = () => {
		// Prepare transaction response
		const transactionResponse = {
			id: transactionId,
			status: transactionData?.transactionStatus || "unknown",
			more_info_url: "",
			stellar_transaction_id: "",
			external_transaction_id: transactionId,
		};

		// Send transaction in legacy format to parent window
		if (window.parent && window.parent !== window) {
			window.parent.postMessage(
				{
					transaction: transactionResponse,
				},
				"*",
			);
		}

		// Also use the existing WebView integration for additional compatibility
		// For deposit, use confirmationNumber; for withdraw, use referenceNumber
		const kindUpper = transactionData?.kind?.toUpperCase();
		const isDeposit = kindUpper === "DEPOSIT";
		const refNumberForWebView =
			isDeposit &&
			(transactionData?.confirmationNumber || transactionData?.confirmationCode)
				? transactionData.confirmationNumber || transactionData.confirmationCode
				: transactionData?.referenceNumber || "";

		sendToWebView("TRANSACTION_STATUS_VIEWED", {
			transactionId: transactionId,
			status: transactionData?.transactionStatus || "unknown",
			referenceNumber: refNumberForWebView,
			token: authToken || "",
			message: "Transaction status viewed",
			timestamp: new Date().toISOString(),
		});
	};

	const handleCancelTransaction = () => {
		// Belt-and-braces: if a refund commit was already submitted for this
		// transaction in the current session, refuse to navigate to /cancel
		// again. The visibility gate in Status.tsx should already hide the
		// button, but this protects against stale prop wiring or any path
		// that bypasses the gate (e.g., handler held by a memoized closure).
		if (hasCancellationBeenSubmitted(transactionId)) return;
		// Navigate to cancellation flow with transaction data
		// For deposit: use confirmationNumber/confirmationCode, for withdraw: use referenceNumber
		// IMPORTANT: For v3 transactions, don't pass refNumber to avoid v2 detection
		const kindUpper = transactionData?.kind?.toUpperCase();
		const isDeposit = kindUpper === "DEPOSIT";
		const isV3 = store.apiVersion === "v3";

		// Only include refNumber for v2/v5 transactions (v3 doesn't need it)
		const refNumber = isV3
			? undefined
			: search.refNumber ||
				(isDeposit &&
				(transactionData?.confirmationNumber ||
					transactionData?.confirmationCode)
					? transactionData.confirmationNumber ||
						transactionData.confirmationCode
					: transactionData?.referenceNumber);

		navigate({
			to: "/ramps/cancel",
			search: {
				transaction_id: transactionId,
				...(refNumber && { refNumber }), // Only include refNumber if it exists and not v3
				lang: store.lang || "en",
				token: search.token,
				accesstoken: search.accesstoken,
				// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
			} as any,
		});
	};

	if (isLoading) {
		return <LoadingSpinner size="lg" />;
	}

	if (hasError || !transactionData) {
		return (
			<div className="flex min-h-dvh flex-col items-center justify-center px-4">
				<RampsText variant="title-lg-bold" color="error" className="mb-4">
					{t`Error Loading Transaction`}
				</RampsText>
				<RampsText
					variant="body-default"
					color="secondary"
					className="mb-8 text-center"
				>
					{t`Unable to load transaction details. Please check your link and try again.`}
				</RampsText>
				<RampsButton variant="primary" onClick={handleClose}>
					{t`Go Back`}
				</RampsButton>
			</div>
		);
	}

	return (
		<div
			className="flex min-h-dvh flex-col"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			<Status
				statusDetails={convertTransactionDataToStatusDetails(transactionData)}
				activeTransactionId={transactionId}
				onCancelTransaction={handleCancelTransaction}
				isStandalone={isStandaloneTransactionStatus}
			/>
		</div>
	);
}
